import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Download, Trash2, Eye, LogOut, Mail, MailOpen,
  Inbox, ChevronLeft, ChevronRight, Loader2, RefreshCw, X,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-luxe">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${accent}`} strokeWidth={1.5} />
      </div>
      <p className="mt-3 font-serif-display text-4xl font-semibold text-foreground">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0 });
  const [viewItem, setViewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(1), [debounced, status, dateFrom, dateTo]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/feedback/stats");
      setStats(data);
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (debounced) params.search = debounced;
      if (status !== "all") params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/feedback", { params });
      setItems(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load feedback.");
      }
    } finally {
      setLoading(false);
    }
  }, [page, debounced, status, dateFrom, dateTo, logout, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats, items]);

  const toggleRead = async (item, e) => {
    e?.stopPropagation();
    try {
      await api.patch(`/feedback/${item.id}`, { read: !item.read });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: !i.read } : i)));
      toast.success(item.read ? "Marked as unread" : "Marked as read");
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/feedback/${deleteItem.id}`);
      toast.success("Feedback deleted");
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const openView = async (item) => {
    setViewItem(item);
    if (!item.read) {
      try {
        await api.patch(`/feedback/${item.id}`, { read: true });
        setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, read: true } : i)));
      } catch (e) { /* ignore */ }
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/feedback/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "hypotho_feedback.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const filtersActive = useMemo(() => search || dateFrom || dateTo || status !== "all", [search, dateFrom, dateTo, status]);

  return (
    <div className="min-h-screen bg-muted/40">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden rounded-full bg-primary/5 px-3 py-1 text-xs font-medium text-primary sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-serif-display text-3xl font-semibold text-foreground md:text-4xl">Feedback Management</h1>
            <p className="mt-2 text-muted-foreground">Review, manage and export every submission.</p>
          </div>
          <button
            onClick={exportCsv}
            disabled={exporting}
            data-testid="export-csv"
            className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors duration-300 hover:bg-gold disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </button>
        </div>

        {/* Stats */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <StatCard label="Total" value={stats.total} icon={Inbox} accent="text-primary" />
          <StatCard label="Unread" value={stats.unread} icon={Mail} accent="text-gold" />
          <StatCard label="Read" value={stats.read} icon={MailOpen} accent="text-muted-foreground" />
        </div>

        {/* Controls */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-4 shadow-luxe lg:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <Tabs value={status} onValueChange={setStatus} className="w-full lg:w-auto">
              <TabsList className="bg-muted" data-testid="status-tabs">
                <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                <TabsTrigger value="unread" data-testid="tab-unread">Unread</TabsTrigger>
                <TabsTrigger value="read" data-testid="tab-read">Read</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, email, subject"
                  data-testid="search-input"
                  className="h-11 w-full rounded-lg border-input bg-background pl-9 sm:w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" className="h-11 rounded-lg border-input bg-background text-sm" />
                <span className="text-muted-foreground">–</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" className="h-11 rounded-lg border-input bg-background text-sm" />
              </div>
              {filtersActive && (
                <button
                  onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setStatus("all"); }}
                  data-testid="clear-filters"
                  className="flex items-center gap-1 rounded-lg border border-border px-3 py-2.5 text-sm text-muted-foreground hover:text-primary"
                >
                  <X className="h-4 w-4" /> Clear
                </button>
              )}
              <button onClick={load} className="rounded-lg border border-border p-2.5 text-muted-foreground hover:text-primary" data-testid="refresh" aria-label="Refresh">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="hidden md:table-cell">Company</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead className="hidden lg:table-cell">Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-16 text-center">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-16 text-center text-muted-foreground" data-testid="empty-state">
                      No feedback found.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id}
                      onClick={() => openView(item)}
                      data-testid={`feedback-row-${item.id}`}
                      className={`cursor-pointer ${item.read ? "" : "bg-gold/5"}`}
                    >
                      <TableCell>
                        <span className={`inline-block h-2.5 w-2.5 rounded-full ${item.read ? "bg-border" : "bg-gold"}`} title={item.read ? "Read" : "Unread"} />
                      </TableCell>
                      <TableCell className={`font-medium ${item.read ? "text-foreground" : "text-foreground"}`}>{item.name}</TableCell>
                      <TableCell className="text-muted-foreground">{item.email}</TableCell>
                      <TableCell className="hidden text-muted-foreground md:table-cell">{item.company || "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate text-foreground">{item.subject}</TableCell>
                      <TableCell className="hidden whitespace-nowrap text-sm text-muted-foreground lg:table-cell">{fmtDate(item.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={(e) => { e.stopPropagation(); openView(item); }} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-primary" data-testid={`view-${item.id}`} aria-label="View">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button onClick={(e) => toggleRead(item, e)} className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-gold" data-testid={`toggle-read-${item.id}`} aria-label="Toggle read">
                            {item.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteItem(item); }} className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid={`delete-${item.id}`} aria-label="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="result-count">{total} result{total === 1 ? "" : "s"}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="prev-page"
                className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 disabled:opacity-40 hover:text-primary"
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="px-2">Page {page} / {pages}</span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page >= pages}
                data-testid="next-page"
                className="flex items-center gap-1 rounded-lg border border-border px-3 py-2 disabled:opacity-40 hover:text-primary"
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* View dialog */}
      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent className="max-w-lg border-border bg-card" data-testid="view-dialog">
          {viewItem && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant={viewItem.read ? "secondary" : "default"} className={viewItem.read ? "" : "bg-gold text-white"}>
                    {viewItem.read ? "Read" : "Unread"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{fmtDate(viewItem.created_at)}</span>
                </div>
                <DialogTitle className="mt-2 font-serif-display text-2xl text-foreground">{viewItem.subject}</DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  From {viewItem.name} · {viewItem.email}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Phone</p><p className="text-foreground">{viewItem.phone || "—"}</p></div>
                  <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Company</p><p className="text-foreground">{viewItem.company || "—"}</p></div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Message</p>
                  <p className="mt-1 whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-4 leading-relaxed text-foreground">{viewItem.message}</p>
                </div>
              </div>
              <div className="mt-2 flex gap-3">
                <a href={`mailto:${viewItem.email}?subject=Re: ${encodeURIComponent(viewItem.subject)}`} className="flex-1 rounded-full bg-primary px-4 py-2.5 text-center text-sm font-semibold text-primary-foreground transition-colors hover:bg-gold">
                  Reply via Email
                </a>
                <button onClick={() => { toggleRead(viewItem); setViewItem((v) => v ? { ...v, read: !v.read } : v); }} className="rounded-full border border-border px-4 py-2.5 text-sm text-foreground hover:border-primary hover:text-primary">
                  Mark {viewItem.read ? "Unread" : "Read"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent className="border-border bg-card" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Delete this feedback?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This will permanently remove the submission from {deleteItem?.name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
